import React, { useState, useEffect } from 'react';
import CustomDropdown from '../../components/CustomDropdown';
import axios from 'axios';
import { baseUrl } from '../../app/api/apiSlice';
import { useSelector } from 'react-redux';

import {
  Header,
  Label,
  Button,
  Icon,
  Popup,
  Loader,
  Table,
  Segment,
  Grid,
  Pagination,
  Container,
  Modal,
  Card,
  Input,
  Dropdown,
  Message,
  Checkbox,
  Form,
} from 'semantic-ui-react';
import { useLocation } from 'react-router-dom';
import { useNavigate, Link } from 'react-router-dom';

import { useGetChatSessionsQuery } from './chatSessionApiSlice';
import { useCreateEvaluationSessionMutation } from '../evaluation_sessions/evaluationSessionApiSlice';
import { useGetEvaluationCriteriaGroupsQuery } from '../evaluation_criteria_group/evaluationCriteriaGroupApiSlice';
import { useGetContractorsListQuery } from '../users/usersApiSlice';

import { Badge, Pill, ui } from '../../components/adminUi';
import ChatSessionDetail from './ChatSessionDetail';
import IdentityReviewModal from './IdentityReviewModal';
import MobileChatSessionList from './MobileChatSessionList';
import { PAGE_SIZE, formatDate, canEvaluate } from '../../constants';
import { NAV } from '../../navLabels';
import '../../components/SharedCard.css';
import CustomSnackbar from '../../components/CustomSnackbar';
import { uploadFileToS3Presigned } from '../../utils/presignedUpload';

// 신원확인은 PII 비식별 파트너에는 해당하지 않는다 — 그 파트너는 신원 정보를 다루지 않는다.
// Keyed off the *session trainee's* contractor, not the viewer's: a superuser sees sessions
// from several contractors at once, so this can differ row to row.
/** The column is Korean; the API's statuses are not. */
/** Same thresholds and palette as 직원 관리's 평균 점수, so one score means one colour
 *  wherever it appears. */
export const scoreColor = score =>
  score >= 80 ? '#1A7F4B' : score >= 50 ? '#B25E00' : '#C0392B';

export const IDENTITY_LABELS = {
  VERIFIED: '확인됨',
  FLAGGED: '이상 있음',
  PENDING: '확인 전',
};
export const IDENTITY_TONES = { VERIFIED: 'green', FLAGGED: 'red', PENDING: 'gray' };

export const identityReviewable = chatSession =>
  !chatSession?.trainee?.contractor?.anonymize_pii;

const ChatSessionsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const token = useSelector(state => state.auth.token);
  const user = useSelector(state => state.auth.user);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get('page');
  const page_size = queryParams.get('page_size');
  const persona = queryParams.get('persona');
  const trainee = queryParams.get('trainee');
  const partner = queryParams.get('partner');
  const contractor = queryParams.get('contractor');
  const start_date = queryParams.get('start_date');
  const end_date = queryParams.get('end_date');
  const search = queryParams.get('search');
  const ordering = queryParams.get('ordering');
  const product = queryParams.get('product');

  const [searchValue, setSearchValue] = useState(search || '');

  // get query params from frontend url -> remove null ones to pass to backend
  let qp = {
    page,
    page_size,
    persona,
    trainee,
    partner,
    search,
    ordering,
    contractor,
    start_date,
    end_date,
    product,
    // Sessions with no lines are noise here; exports send the same filters.
    has_lines: 1,
  };
  qp = Object.entries(qp)
    .filter(e => e[1])
    .reduce((accumulator, [k, v]) => {
      accumulator[k] = v;
      return accumulator;
    }, {});

  const { data: contractorsData } = useGetContractorsListQuery();
  const contractors = Array.isArray(contractorsData)
    ? contractorsData
    : contractorsData?.results || [];
  const contractorOptions = contractors.map(c => ({
    key: c.id,
    text: c.name,
    value: c.id,
  }));

  const handleFilterChange = (key, value) => {
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSearch = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchValue) {
      searchParams.set('search', searchValue);
    } else {
      searchParams.delete('search');
    }
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSort = clickedColumn => {
    const searchParams = new URLSearchParams(location.search);
    const currentOrdering = searchParams.get('ordering');

    let newOrdering = clickedColumn;
    if (currentOrdering === clickedColumn) {
      newOrdering = `-${clickedColumn}`;
    } else if (currentOrdering === `-${clickedColumn}`) {
      newOrdering = clickedColumn;
    }

    searchParams.set('ordering', newOrdering);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const getSortIcon = column => {
    if (ordering === column) return 'sort up';
    if (ordering === `-${column}`) return 'sort down';
    return 'sort';
  };

  // Zoom Upload Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    contractor: '',
    isNewUser: false,
    selectedUserUsername: '',
    file: null,
  });
  const [uploadStatus, setUploadStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });

  useEffect(() => {
    if (modalOpen) {
      // Fetch users
      axios
        .get(`${baseUrl}/api/v1/accounts/users/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(res => {
          const results = res.data.results || res.data;
          const formatted = (Array.isArray(results) ? results : []).map(u => ({
            key: u.id,
            text: `${u.full_name || u.username} (${u.username})`,
            value: u.username,
          }));
          setUsersList(formatted);
        })
        .catch(err => console.error('Failed to fetch users', err));
    }
  }, [modalOpen, token]);

  const handleUploadSubmit = async () => {
    if (!formData.file) {
      setUploadStatus({
        loading: false,
        error: '파일을 선택해주세요.',
        success: false,
      });
      return;
    }

    if (!formData.contractor) {
      setUploadStatus({
        loading: false,
        error: '파트너를 선택해주세요.',
        success: false,
      });
      return;
    }

    const targetUsername = formData.isNewUser
      ? formData.username
      : formData.selectedUserUsername;
    if (!targetUsername) {
      setUploadStatus({
        loading: false,
        error: '사용자를 선택하거나 입력해주세요.',
        success: false,
      });
      return;
    }

    setUploadStatus({ loading: true, error: null, success: false });
    try {
      // 1. Upload file to S3 (presigned)
      const result = await uploadFileToS3Presigned(formData.file, token);
      if (!result.success) {
        throw new Error(result.error || 'S3 upload failed');
      }

      // 2. Confirm upload and trigger processing
      const confirmRes = await axios.post(
        `${baseUrl}/api/confirm-upload/`,
        {
          key: result.key,
          username: targetUsername,
          contractor_id: formData.contractor,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!confirmRes.data.task_id) {
        throw new Error('Failed to start processing task');
      }

      setUploadStatus({ loading: false, error: null, success: true });
      setTimeout(() => {
        setModalOpen(false);
        setUploadStatus({ loading: false, error: null, success: false });
        setFormData({
          username: '',
          contractor: '',
          isNewUser: false,
          selectedUserUsername: '',
          file: null,
        });
      }, 2000);
    } catch (err) {
      console.error(err);
      setUploadStatus({
        loading: false,
        error: err.response?.data?.error || '업로드 중 오류가 발생했습니다.',
        success: false,
      });
    }
  };

  // GET chat sessions
  const { data, isLoading, isSuccess, isError, error } =
    useGetChatSessionsQuery(
      { ...qp },
      {
        pollingInterval: 5000,
        // Phones use MobileChatSessionList, which fetches its own pages.
        skip: isMobile,
      },
    );

  // to handle chat session selection
  // const [selectedChatSession, setSelectedChatSession] = useState(null);
  const [snackbarVisible, setSnackbarVisible] = useState(false);

  const isAdmin = user?.is_staff || user?.is_superuser || user?.is_contractor_admin;
  const showIdentityColumn =
    isAdmin && (data?.results || []).some(identityReviewable);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [sessionToReview, setSessionToReview] = useState(null);
  const [reviewSnackbarVisible, setReviewSnackbarVisible] = useState(false);

  const handleSelectChatSession = chat_session => {
    if (chat_session.evaluation_session_count > 0) {
      navigate(`/chatsessions/${chat_session.id}`);
    } else {
      setSnackbarVisible(true);
      setTimeout(() => setSnackbarVisible(false), 3000);
    }
  };

  // To make new evaluation sessions when the user click the button
  const { data: criteria_groups, isLoading: isLoading_cg } =
    useGetEvaluationCriteriaGroupsQuery();
  const [createEvaluationSession, { isSuccess: isSuccess_eval }] =
    useCreateEvaluationSessionMutation();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [sessionToEvaluate, setSessionToEvaluate] = useState(null);

  const handleOpenConfirmModal = chat_session => {
    setSessionToEvaluate(chat_session);
    setConfirmModalOpen(true);
  };

  const handleConfirmEvaluation = () => {
    if (sessionToEvaluate && criteria_groups?.results?.length > 0) {
      createEvaluationSession({
        chat_session: sessionToEvaluate.id,
        criteria_group: criteria_groups.results[0].id,
      });
      setConfirmModalOpen(false);
      setSessionToEvaluate(null);
    }
  };
  useEffect(() => {
    if (isSuccess_eval) {
      navigate(`/evaluationsessionslist?page=1&page_size=${PAGE_SIZE}`);
    }
  }, [isSuccess_eval, navigate]);

  // Same pill geometry as the 녹음 파일 업로드 CTA next to it, in the neutral tone from
  // adminUi so the primary action still reads as primary.
  const exportButtonStyle = {
    borderRadius: '30px',
    height: '40px',
    backgroundColor: '#F2F2F7',
    color: '#1d1d1f',
    paddingLeft: '20px',
    paddingRight: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: isMobile ? '8px' : 0,
    alignSelf: isMobile ? 'stretch' : 'auto',
  };

  // Excel export (client request 17: 개별 및 일괄 다운로드).
  //
  // Server-generated, so the layout can match the ACE Challenge workbooks Apple sent us
  // and the browser needs no spreadsheet library. It has to go through fetch rather than
  // a plain link because the API is bearer-authenticated — an <a href> would 401.
  //
  // The current filter set is sent as-is, minus paging, so the file matches the screen.
  const [exporting, setExporting] = useState(null);
  const [exportFailed, setExportFailed] = useState(false);
  const handleExport = async (kind, extraParams = {}) => {
    setExporting(kind);
    try {
      const params = new URLSearchParams({ ...qp, ...extraParams });
      params.delete('page');
      params.delete('page_size');
      const response = await fetch(
        `${baseUrl}/api/v1/resources/chat_sessions/detailed/export_${kind}/?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // Named here rather than from Content-Disposition: the API is a different origin,
      // and that header is not exposed to JS unless the server sets
      // Access-Control-Expose-Headers. Building it client-side keeps the date stamp
      // without adding a CORS setting that ops would have to remember.
      const stamp = new Date()
        .toLocaleDateString('sv-SE')  // sv-SE is ISO-shaped: 2026-08-03
        .replace(/-/g, '');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `session_${kind}_${stamp}.xlsx`;
      // Appended before clicking: a detached anchor does not reliably trigger a
      // download outside Chrome, which is why the old dead code here was unreliable.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Deferred so the download has actually started before the blob is released.
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (err) {
      setExportFailed(true);
    } finally {
      setExporting(null);
    }
  };

  const handlePaginationChange = (e, { activePage }) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', activePage);
    // page_size is usually preserved or set explicitly.
    // The previous code hardcoded PAGE_SIZE.
    if (!searchParams.get('page_size')) {
      searchParams.set('page_size', PAGE_SIZE);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  // display language is in korean
  if (isLoading || isLoading_cg) return <Loader active />;
  if (isError) return null;

  return (
    <div
      style={{
        // backgroundColor: '#f9fafb',
        // minHeight: '100vh',
        height: isMobile ? 'auto' : 'auto',
        display: isMobile ? 'block' : 'flex',
        flexDirection: 'column',
        overflow: isMobile ? 'visible' : 'hidden',
      }}
    >
      {/* Phones: the same header band as 가상 고객과 롤플레이 and 제품 지식 퀴즈 — title and
          subtitle inside the same Container as the list, so both start at its 14px gutter. */}
      {isMobile && (
        <div style={{ padding: '16px 0', backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,0,0,0.05)' }} className="page-header-band">
          <Container>
            <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>{NAV.SESSIONS}</h2>
            <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>{NAV.SESSIONS_SUBTITLE}</p>
          </Container>
        </div>
      )}
      {/* Header Section (desktop) */}
      {!isMobile && (
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          marginBottom: isMobile ? '20px' : '0',
        }}
      >
        <Container fluid>
          <div
            style={{
              display: 'flex',
              alignItems: isMobile ? 'flex-start' : 'center',
              justifyContent: isMobile ? 'flex-start' : 'space-between',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px',
              padding: '0 20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  width: '40px',
                  flexShrink: 0, // stays a circle when the text beside it wraps
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#F2F2F7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon
                  name="comments outline"
                  style={{ margin: 0, color: '#007AFF', fontSize: '18px' }}
                />
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '17px',
                    fontWeight: '600',
                    margin: 0,
                    color: '#1d1d1f',
                  }}
                >
                  {NAV.SESSIONS}
                </h2>
                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                  {NAV.SESSIONS_SUBTITLE}
                </p>
              </div>
            </div>

            {/* One group, so the parent's space-between separates title | actions
                rather than spreading four children across the whole row. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexDirection: isMobile ? 'column' : 'row',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              {isAdmin && (
                <>
                  {/* `icon labelPosition="right"` is what 녹음 파일 업로드 uses and what
                      these two were missing: without it Semantic falls back to
                      `.ui.button > .icon`, which sets vertical-align on an inline glyph
                      and leaves it sitting off the text's centre. With it, the icon gets
                      its own full-height block. Same props, same result — only the colour
                      differs, which is the point. */}
                  <Button
                    icon
                    labelPosition="right"
                    loading={exporting === 'summary'}
                    disabled={!!exporting}
                    onClick={() => handleExport('summary')}
                    style={exportButtonStyle}
                  >
                    요약 다운로드
                    <Icon name="file alternate outline" style={{ margin: 0 }} />
                  </Button>
                  {/* The workbook holds one 평가표 per evaluated session, so it can have
                      fewer sheets than the list has rows — say so before they count. */}
                  <Popup
                    content="평가 완료된 세션만 평가표로 받습니다."
                    trigger={
                      <Button
                        icon
                        labelPosition="right"
                        loading={exporting === 'detail'}
                        disabled={!!exporting}
                        onClick={() => handleExport('detail')}
                        style={exportButtonStyle}
                      >
                        상세 다운로드
                        {/* Stacked pages, not the single page 요약 uses: this workbook is
                            one 평가표 sheet per session, so the icon says how they differ
                            rather than making two buttons that look interchangeable. */}
                        <Icon name="copy outline" style={{ margin: 0 }} />
                      </Button>
                    }
                  />
                </>
              )}
              {/* Admin only, as on phones: uploading a store recording for evaluation. */}
              {isAdmin && (
              <Button
                primary
                icon
                labelPosition="right"
                onClick={() => setModalOpen(true)}
                style={{
                  borderRadius: '30px',
                  height: '40px',
                  backgroundColor: '#0091FF',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: isMobile ? '8px' : 0,
                  alignSelf: isMobile ? 'stretch' : 'auto',
                }}
              >
                녹음 파일 업로드
                <Icon name="upload" style={{ marginLeft: '8px', margin: 0 }} />
              </Button>
              )}
            </div>
          </div>
        </Container>
      </div>
      )}

      <Container
        fluid
        style={{
          padding: isMobile ? 0 : '20px',
          paddingBottom: isMobile ? 0 : '20px',
          flex: isMobile ? 'none' : 1,
          display: isMobile ? 'block' : 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {!isMobile && (
        <div
          style={{
            marginBottom: '24px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: isMobile ? 'stretch' : 'center',
          }}
        >
          {/* Date Range Picker */}
          <div
            style={{
              flex: '0 1 auto',
              display: isMobile ? 'grid' : 'flex',
              alignItems: isMobile ? 'stretch' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: isMobile ? '8px 16px' : '0 16px',
              gap: '8px',
              height: isMobile ? 'auto' : '48px',
              position: isMobile ? 'static' : 'static',
              gridTemplateColumns: isMobile ? '1fr auto' : undefined,
              gridTemplateRows: isMobile ? 'auto auto' : undefined,
            }}
          >
            <Input
              type="date"
              value={queryParams.get('start_date') || ''}
              onChange={e => handleFilterChange('start_date', e.target.value)}
              transparent
              style={{
                fontSize: '14px',
                width: isMobile ? '100%' : '160px',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: isMobile ? '8px' : 0,
                gap: '8px',
                gridColumn: isMobile ? '1 / 2' : undefined,
              }}
            >
              <span
                style={{
                  color: '#86868b',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                -
              </span>
              <Input
                type="date"
                value={queryParams.get('end_date') || ''}
                onChange={e => handleFilterChange('end_date', e.target.value)}
                transparent
                style={{
                  fontSize: '14px',
                  width: isMobile ? '100%' : '160px',
                  fontFamily: 'inherit',
                  color: '#1d1d1f',
                }}
              />
            </div>
            {(queryParams.get('start_date') || queryParams.get('end_date')) && (
              <Icon
                name="close"
                size={'small'}
                style={{
                  borderWidth: 0,
                  border: 0,
                  color: '#86868b',
                  margin: 0,
                  marginLeft: isMobile ? 0 : '4px',
                  position: 'static',
                  alignSelf: isMobile ? 'center' : 'auto',
                  gridRow: isMobile ? '1 / span 2' : 'auto',
                  gridColumn: isMobile ? '2 / 3' : 'auto',
                  minWidth: isMobile ? 44 : undefined,
                  minHeight: isMobile ? 44 : undefined,
                  display: isMobile ? 'flex' : undefined,
                  alignItems: isMobile ? 'center' : undefined,
                  justifyContent: isMobile ? 'center' : undefined,
                }}
                onClick={() => {
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.delete('start_date');
                  searchParams.delete('end_date');
                  searchParams.set('page', 1);
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              />
            )}
          </div>
          {/* Partner Dropdown (Admin only) */}
          {(user?.is_staff || user?.is_superuser) && (
            <CustomDropdown
              placeholder="파트너 선택"
              options={contractorOptions}
              value={contractor}
              onChange={value => handleFilterChange('contractor', value)}
              icon="building outline"
              style={{ minWidth: '200px' }}
            />
          )}
          <div
            style={{
              flex: '1 1 auto',
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'flex-start',
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '0 16px',
              height: '48px',
              gap: '12px',
              transition: 'all 0.2s ease',
            }}
          >
            <Icon
              name="search"
              style={{ color: '#86868b', margin: 0, fontSize: '16px' }}
            />
            <Input
              placeholder="검색 (제품, 페르소나, 내용)..."
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              transparent
              fluid
              style={{
                fontSize: '13px',
                width: '100%',
                fontFamily: 'inherit',
                color: '#1d1d1f',
              }}
            />
            {searchValue && (
              <Icon
                name="close"
                link
                style={{
                  color: '#86868b',
                  margin: 0,
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={e => {
                  e.stopPropagation();
                  setSearchValue('');
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.delete('search');
                  navigate(`${location.pathname}?${searchParams.toString()}`);
                }}
              />
            )}
          </div>
          {/* Search Button */}
          <div
            style={{
              marginLeft: isMobile ? '0' : 'auto',
              width: isMobile ? '100%' : 'auto',
              display: 'flex',
              gap: '8px',
            }}
          >
            <Button
              primary
              icon
              labelPosition="right"
              onClick={handleSearch}
              fluid={isMobile}
              style={{
                borderRadius: '30px',
                height: '48px',
                backgroundColor: '#0091FF',
                paddingLeft: '16px',
                paddingRight: '16px',
                // display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              검색
              <Icon
                name="arrow right"
                style={{ marginLeft: '8px', margin: 0 }}
              />
            </Button>
          </div>
        </div>
        )}
        {isMobile ? (
          <MobileChatSessionList
            isAdmin={isAdmin}
            onOpen={handleSelectChatSession}
            onEvaluate={handleOpenConfirmModal}
            onUpload={() => setModalOpen(true)}
          />
        ) : (
          <div style={{ height: '100%', margin: 0 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.05)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                overflowY: 'auto',
                // maxHeight: '100%',
              }}
            >
              <div>
                <Table
                  compact="very"
                  selectable
                  style={{ margin: 0, border: 'none' }}
                >
                  <Table.Header
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: 'white',
                    }}
                  >
                    <Table.Row>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          paddingLeft: '20px',
                        }}
                      >
                        ID
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('created_at')}
                      >
                        일시 <Icon name={getSortIcon('created_at')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('persona__name')}
                      >
                        페르소나 <Icon name={getSortIcon('persona__name')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('trainee__username')}
                      >
                        직원 <Icon name={getSortIcon('trainee__username')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{ borderBottom: '1px solid rgba(34,36,38,.15)' }}
                      >
                        파트너
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('is_practice')}
                      >
                        구분 <Icon name={getSortIcon('is_practice')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('chatlog_count')}
                      >
                        발화 수 <Icon name={getSortIcon('chatlog_count')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('duration')}
                      >
                        소요시간 <Icon name={getSortIcon('duration')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('latest_evaluation_score')}
                      >
                        응대스킬{' '}
                        <Icon name={getSortIcon('latest_evaluation_score')} />
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('latest_pk_evaluation_score')}
                      >
                        제품지식{' '}
                        <Icon
                          name={getSortIcon('latest_pk_evaluation_score')}
                        />
                      </Table.HeaderCell>
                      {showIdentityColumn && (
                        <Table.HeaderCell
                          style={{ borderBottom: '1px solid rgba(34,36,38,.15)', textAlign: 'center' }}
                        >
                          신원확인
                        </Table.HeaderCell>
                      )}
                      {/* One column per action, so a button always sits in the same place
                          and they line up down the table. Sharing one cell made them shift
                          horizontally row to row, since each is conditional. */}
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          textAlign: 'center',
                        }}
                      >
                        이어하기
                      </Table.HeaderCell>
                      <Table.HeaderCell
                        style={{
                          borderBottom: '1px solid rgba(34,36,38,.15)',
                          textAlign: 'center',
                        }}
                      >
                        평가
                      </Table.HeaderCell>
                      {isAdmin && (
                        <Table.HeaderCell
                          style={{
                            borderBottom: '1px solid rgba(34,36,38,.15)',
                            textAlign: 'center',
                          }}
                        >
                          다운로드
                        </Table.HeaderCell>
                      )}
                    </Table.Row>
                  </Table.Header>

                  <Table.Body>
                    {data.results.map(chat_session => (
                      <Table.Row
                        key={chat_session.id}
                        onClick={() => handleSelectChatSession(chat_session)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Table.Cell style={{ paddingLeft: '20px' }}>
                          {/* A support handle, not a status chip. It was a solid grey pill
                              carrying the same visual weight as 구분 and the scores. */}
                          <span
                            onClick={e => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(chat_session.id);
                            }}
                            title="클릭하면 세션 ID가 복사됩니다"
                            style={{
                              cursor: 'pointer',
                              color: ui.muted,
                              fontSize: '12px',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            }}
                          >
                            {chat_session.id.slice(0, 5)}
                          </span>
                        </Table.Cell>
                        <Table.Cell>
                          {formatDate(chat_session.created_at)}
                        </Table.Cell>
                        <Table.Cell>
                          {/* N번 고객 — what the trainee saw, what the client cites, and what
                              the exported sheets are named by. The generated name and the
                              visit reason move into the tooltip; the gender icon is gone for
                              the same reason it left 직원 관리. */}
                          {chat_session.persona ? (
                            <Popup
                              header={chat_session.persona.name}
                              content={
                                chat_session.persona.persona?.purpose_of_visit?.value ||
                                chat_session.persona.purpose_of_visit
                              }
                              trigger={
                                <span>
                                  {chat_session.persona.int_id
                                    ? `${chat_session.persona.int_id}번 고객`
                                    : chat_session.persona.name}
                                </span>
                              }
                            />
                          ) : (
                            <span style={{ color: ui.muted }}>실제 고객</span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {chat_session.trainee ? (
                            <Link
                              to={`/userdashboard?userId=${chat_session.trainee.id}`}
                              onClick={e => e.stopPropagation()}
                              style={{ color: '#0091FF' }}
                            >
                              <Icon name="user circle" />
                              {chat_session.trainee.full_name ||
                                chat_session.trainee.username}
                            </Link>
                          ) : (
                            <span style={{ color: 'grey' }}>실제 인물</span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {chat_session.trainee?.contractor ? (
                            <span>{chat_session.trainee.contractor.name}</span>
                          ) : (
                            <span style={{ color: 'grey' }}>-</span>
                          )}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <Badge tone={chat_session.is_practice ? 'gray' : 'blue'}>
                            {chat_session.is_practice ? '연습' : '평가'}
                          </Badge>
                        </Table.Cell>
                        {/* Values, not states: a turn count and a duration in outlined
                            boxes read like status chips and competed with the ones that
                            are. */}
                        <Table.Cell textAlign="center">{chat_session.chatlog_count}</Table.Cell>
                        <Table.Cell textAlign="center" style={{ color: ui.muted }}>
                          {chat_session.duration ? chat_session.duration.split('.')[0] : '—'}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {chat_session.latest_evaluation_score != null ? (
                            <span style={{ fontWeight: 600, color: scoreColor(chat_session.latest_evaluation_score) }}>
                              {chat_session.latest_evaluation_score.toFixed(1)}
                            </span>
                          ) : (
                            <span style={{ color: ui.muted }}>—</span>
                          )}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {chat_session.latest_pk_evaluation_score != null ? (
                            <span style={{ fontWeight: 600, color: scoreColor(chat_session.latest_pk_evaluation_score) }}>
                              {chat_session.latest_pk_evaluation_score.toFixed(1)}
                            </span>
                          ) : (
                            <span style={{ color: ui.muted }}>—</span>
                          )}
                        </Table.Cell>

                        {showIdentityColumn && (
                          <Table.Cell textAlign="center" onClick={e => e.stopPropagation()}>
                            {!identityReviewable(chat_session) ? (
                              '-'
                            ) : (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setSessionToReview(chat_session.id);
                                setReviewModalOpen(true);
                              }}
                              style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                            >
                              {/* Was a hand-rolled chip repeating adminUi's exact tone
                                  values inline, labelled in English inside an otherwise
                                  Korean table. */}
                              <Badge tone={IDENTITY_TONES[chat_session.identity_verified_status] || 'gray'}>
                                <Icon name="search" style={{ margin: 0, fontSize: 10 }} />
                                {IDENTITY_LABELS[chat_session.identity_verified_status] || '확인 전'}
                              </Badge>
                            </button>
                            )}
                          </Table.Cell>
                        )}
                        <Table.Cell
                          textAlign="center"
                          onClick={e => e.stopPropagation()}
                        >
                          <Popup
                            content={
                              // Lines added after grading wouldn't be in the score.
                              chat_session.evaluation_session_count > 0
                                ? '이미 평가가 실행된 세션은 이어할 수 없습니다'
                                : '이 채팅 세션을 이어합니다.'
                            }
                            trigger={
                              <Pill
                                variant="neutral"
                                disabled={chat_session.evaluation_session_count > 0}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (chat_session.evaluation_session_count > 0) return;
                                  navigate(`/audio-orb-openai?session_id=${chat_session.id}`);
                                }}
                              >
                                <Icon name="comments" style={{ margin: 0 }} /> 이어하기
                              </Pill>
                            }
                          />
                        </Table.Cell>
                        <Table.Cell
                          textAlign="center"
                          onClick={e => e.stopPropagation()}
                        >
                          {/* 연습 sessions are never graded — the server refuses them. */}
                          {chat_session.is_practice ? (
                            '-'
                          ) : (
                            <Popup
                              content={
                                chat_session.chatlog_count < 6
                                  ? '평가를 위해 최소 3턴 이상 필요'
                                  : chat_session.evaluation_session_count > 0
                                    ? '이미 평가가 실행된 세션입니다'
                                    : '평가 실행'
                              }
                              trigger={
                                // Primary only when it is the first evaluation and the
                                // session is long enough — otherwise this is one of three
                                // buttons in a row and none of them would lead.
                                <Pill
                                  variant={
                                    canEvaluate(chat_session) ? 'primary' : 'neutral'
                                  }
                                  disabled={!canEvaluate(chat_session)}
                                  onClick={e => {
                                    e.stopPropagation();
                                    if (canEvaluate(chat_session))
                                      handleOpenConfirmModal(chat_session);
                                  }}
                                >
                                  <Icon name="clipboard check" style={{ margin: 0 }} /> 평가 실행
                                </Pill>
                              }
                            />
                          )}
                        </Table.Cell>
                        {isAdmin && (
                          <Table.Cell
                            textAlign="center"
                            onClick={e => e.stopPropagation()}
                          >
                            {!chat_session.is_practice &&
                            chat_session.evaluation_session_count > 0 ? (
                              <Popup
                                content="이 세션 평가표 다운로드"
                                trigger={
                                  <Pill
                                    variant="neutral"
                                    disabled={!!exporting}
                                    style={{ padding: '7px 10px' }}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleExport('detail', { id: chat_session.id });
                                    }}
                                  >
                                    <Icon name="file alternate outline" style={{ margin: 0 }} />
                                  </Pill>
                                }
                              />
                            ) : (
                              '-'
                            )}
                          </Table.Cell>
                        )}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>

              {/* Pagination inside Left Pane */}
              <div
                style={{
                  padding: '12px',
                  borderTop: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <Pagination
                  activePage={page}
                  onPageChange={handlePaginationChange}
                  totalPages={Math.ceil(data.count / page_size)}
                  size="mini"
                  ellipsisItem={{
                    content: <Icon name="ellipsis horizontal" />,
                    icon: true,
                  }}
                  firstItem={{
                    content: <Icon name="angle double left" />,
                    icon: true,
                  }}
                  lastItem={{
                    content: <Icon name="angle double right" />,
                    icon: true,
                  }}
                  prevItem={{ content: <Icon name="angle left" />, icon: true }}
                  nextItem={{
                    content: <Icon name="angle right" />,
                    icon: true,
                  }}
                />
              </div>
            </div>
          </div>
        )}{' '}
        <Modal
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          size="small"
        >
          <Header icon="magic" content="평가 실행 확인" />
          <Modal.Content>
            {sessionToEvaluate && (
              <>
                <p>이 대화로 평가를 실행하시겠습니까?</p>
                <Table definition>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell width={4}>ID</Table.Cell>
                      <Table.Cell>
                        {sessionToEvaluate.id.slice(0, 8)}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>일시</Table.Cell>
                      <Table.Cell>
                        {formatDate(sessionToEvaluate.created_at)}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>교육생</Table.Cell>
                      <Table.Cell>
                        {sessionToEvaluate.trainee?.full_name ||
                          sessionToEvaluate.trainee?.username}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>페르소나</Table.Cell>
                      <Table.Cell>{sessionToEvaluate.persona?.name}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell>기존 평가 수</Table.Cell>
                      <Table.Cell>
                        {sessionToEvaluate.evaluation_session_count}회
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button color="red" onClick={() => setConfirmModalOpen(false)}>
              <Icon name="remove" /> 취소
            </Button>
            <Button color="green" onClick={handleConfirmEvaluation}>
              <Icon name="checkmark" /> 평가 실행
            </Button>
          </Modal.Actions>
        </Modal>
        <CustomSnackbar
          visible={snackbarVisible}
          title="평가 결과 없음"
          message="이 세션에 대한 평가 결과가 없습니다."
          onClose={() => setSnackbarVisible(false)}
          type="warning"
        />
        {/* Upload Modal */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="tiny">
          <Modal.Header>세션 파일 업로드 (Video/Audio)</Modal.Header>
          <Modal.Content>
            <Form error={!!uploadStatus.error} success={uploadStatus.success}>
              <Form.Field required>
                <label>파트너 선택</label>
                <Dropdown
                  placeholder="파트너 선택"
                  fluid
                  selection
                  options={contractorOptions}
                  value={formData.contractor}
                  onChange={(e, { value }) =>
                    setFormData({ ...formData, contractor: value })
                  }
                />
              </Form.Field>

              <Form.Field>
                <Checkbox
                  toggle
                  label="새로운 사용자 생성"
                  checked={formData.isNewUser}
                  onChange={(e, { checked }) =>
                    setFormData({ ...formData, isNewUser: checked })
                  }
                />
              </Form.Field>

              {formData.isNewUser ? (
                <Form.Field required>
                  <label>새 사용자 이름 (Username)</label>
                  <Input
                    placeholder="Username"
                    value={formData.username}
                    onChange={e =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                  />
                </Form.Field>
              ) : (
                <Form.Field required>
                  <label>기존 사용자 선택</label>
                  <Dropdown
                    placeholder="사용자 검색 및 선택"
                    fluid
                    search
                    selection
                    options={usersList}
                    value={formData.selectedUserUsername}
                    onChange={(e, { value }) =>
                      setFormData({ ...formData, selectedUserUsername: value })
                    }
                  />
                </Form.Field>
              )}

              <Form.Field required>
                <label>녹화 파일 (.mp4, .mp3, .wav, .m4a)</label>
                <Input
                  type="file"
                  accept=".mp4,.mp3,.wav,.m4a"
                  onChange={e =>
                    setFormData({ ...formData, file: e.target.files[0] })
                  }
                />
              </Form.Field>

              <Message error header="오류 발생" content={uploadStatus.error} />
              <Message
                success
                header="업로드 완료"
                content={
                  <>
                    성공적으로 업로드되었습니다. 잠시 후 창이 닫힙니다.<br />
                    파일 크기에 따라 업로드 완료까지 시간이 소요될 수 있습니다.
                  </>
                }
              />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={() => setModalOpen(false)}
              disabled={uploadStatus.loading}
            >
              취소
            </Button>
            <Button
              content="업로드"
              labelPosition="right"
              icon="checkmark"
              onClick={handleUploadSubmit}
              primary
              loading={uploadStatus.loading}
              disabled={uploadStatus.loading || uploadStatus.success}
            />
          </Modal.Actions>
        </Modal>
        {isAdmin && (
          <>
            <IdentityReviewModal
              sessionId={sessionToReview}
              open={reviewModalOpen}
              onClose={() => {
                setReviewModalOpen(false);
                setSessionToReview(null);
              }}
              onReviewSuccess={() => {
                setReviewSnackbarVisible(true);
                setTimeout(() => setReviewSnackbarVisible(false), 3000);
              }}
            />
            <CustomSnackbar
              visible={reviewSnackbarVisible}
              title="신원 확인 완료"
              message="신원 검토가 저장되었습니다."
              onClose={() => setReviewSnackbarVisible(false)}
              type="success"
            />
            <CustomSnackbar
              visible={exportFailed}
              title="다운로드 실패"
              message="엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요."
              onClose={() => setExportFailed(false)}
              type="error"
            />
          </>
        )}
      </Container>
    </div>
  );
};
export default ChatSessionsList;
